<template>
  <header class="site-header" :class="{ scrolled: scrolled }">
    <div class="site-header__inner">
      <!-- Logo -->
      <router-link to="/" class="site-header__logo">
        <img src="@/assets/img/home/logo.png" alt="SherlockIT" class="site-header__logo-img" />
      </router-link>

      <!-- Desktop nav -->
      <nav class="site-header__nav">
        <router-link to="/" class="site-header__link" :class="{ active: $route.path === '/' }">Home</router-link>

        <div class="site-header__dropdown" @mouseenter="searchOpen=true" @mouseleave="searchOpen=false">
          <span class="site-header__link" :class="{ active: $route.path.startsWith('/search') || $route.path.startsWith('/direct') }">
            Search <i class="fa fa-chevron-down site-header__chevron"></i>
          </span>
          <div class="site-header__dropdown-menu" v-show="searchOpen">
            <router-link to="/search" class="site-header__dropdown-item">General Search</router-link>
            <router-link to="/direct-search" class="site-header__dropdown-item">Direct Search</router-link>
          </div>
        </div>

        <router-link to="/coverage" class="site-header__link" :class="{ active: $route.path === '/coverage' }">Coverage</router-link>
        <router-link to="/insights" class="site-header__link" :class="{ active: $route.path.startsWith('/insights') }">Insights</router-link>
        <router-link to="/about-us" class="site-header__link" :class="{ active: $route.path === '/about-us' }">Company</router-link>
        <router-link to="/contact-us" class="site-header__link" :class="{ active: $route.path === '/contact-us' }">Contact</router-link>
      </nav>

      <!-- Right actions -->
      <div class="site-header__actions">
        <template v-if="!$store.getters.auth">
          <router-link to="/login" class="site-header__btn site-header__btn--ghost">Log In</router-link>
          <router-link to="/signup" class="site-header__btn site-header__btn--primary">Try for free</router-link>
        </template>
        <template v-else>
          <router-link v-if="$store.getters.live_data[0]" to="/live" class="site-header__link site-header__link--danger">
            Live <span class="site-header__badge">{{ $store.getters.live_data.length }}</span>
          </router-link>
          <div class="site-header__dropdown" @mouseenter="accountOpen=true" @mouseleave="accountOpen=false">
            <span class="site-header__btn site-header__btn--ghost">
              My Account <i class="fa fa-chevron-down site-header__chevron"></i>
            </span>
            <div class="site-header__dropdown-menu site-header__dropdown-menu--right" v-show="accountOpen">
              <router-link to="/account" class="site-header__dropdown-item"><i class="fa fa-user"></i> Profile</router-link>
              <router-link to="/history" class="site-header__dropdown-item"><i class="fa fa-history"></i> History</router-link>
              <span class="site-header__dropdown-item site-header__dropdown-item--danger" @click="logout"><i class="fa fa-sign-out"></i> Logout</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Mobile hamburger -->
      <button class="site-header__hamburger" @click="mobileOpen = !mobileOpen" aria-label="Menu">
        <span></span><span></span><span></span>
      </button>
    </div>

    <!-- Mobile menu -->
    <div class="site-header__mobile" v-show="mobileOpen">
      <router-link to="/" class="site-header__mobile-link" @click.native="mobileOpen=false">Home</router-link>
      <router-link to="/search" class="site-header__mobile-link" @click.native="mobileOpen=false">General Search</router-link>
      <router-link to="/direct-search" class="site-header__mobile-link" @click.native="mobileOpen=false">Direct Search</router-link>
      <router-link to="/coverage" class="site-header__mobile-link" @click.native="mobileOpen=false">Coverage</router-link>
      <router-link to="/insights" class="site-header__mobile-link" @click.native="mobileOpen=false">Insights</router-link>
      <router-link to="/about-us" class="site-header__mobile-link" @click.native="mobileOpen=false">Company</router-link>
      <router-link to="/contact-us" class="site-header__mobile-link" @click.native="mobileOpen=false">Contact</router-link>
      <div class="site-header__mobile-divider"></div>
      <template v-if="!$store.getters.auth">
        <router-link to="/login" class="site-header__mobile-link" @click.native="mobileOpen=false">Log In</router-link>
        <router-link to="/signup" class="site-header__mobile-link site-header__mobile-link--primary" @click.native="mobileOpen=false">Try for free</router-link>
      </template>
      <template v-else>
        <router-link to="/account" class="site-header__mobile-link" @click.native="mobileOpen=false">Profile</router-link>
        <router-link to="/history" class="site-header__mobile-link" @click.native="mobileOpen=false">History</router-link>
        <span class="site-header__mobile-link site-header__mobile-link--danger" @click="logout">Logout</span>
      </template>
    </div>
  </header>
</template>

<script>
export default {
  data: () => ({
    scrolled: false,
    mobileOpen: false,
    searchOpen: false,
    accountOpen: false,
  }),
  mounted() {
    window.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 8;
    },
    logout() {
      localStorage.removeItem('access');
      localStorage.removeItem('refresh');
      window.location.reload();
    }
  }
};
</script>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: linear-gradient(to bottom, #fff 0%, #fafbff 100%);
  border-bottom: 1px solid #e8edf5;
  transition: box-shadow 0.2s;
}
.site-header.scrolled {
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}

.site-header__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 32px;
}

/* Logo */
.site-header__logo { display: flex; align-items: center; text-decoration: none; flex-shrink: 0; }
.site-header__logo-img { height: 36px; width: auto; }

/* Nav */
.site-header__nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}
.site-header__link {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  white-space: nowrap;
  user-select: none;
  font-family: inherit;
  line-height: 1.5;
}
.site-header__link:hover { color: #0f172a; background: #f1f5f9; text-decoration: none; }
.site-header__link.active { color: #0f172a; font-weight: 600; position: relative; }
.site-header__link.active::after { content: ''; position: absolute; bottom: -4px; left: 14px; right: 14px; height: 2px; background: #575ecf; border-radius: 2px; }
.site-header__link--danger { color: #dc2626; }
.site-header__chevron { font-size: 10px; margin-left: 4px; }

/* Dropdown */
.site-header__dropdown { position: relative; display: flex; align-items: center; }
.site-header__dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  padding: 8px 6px 6px;
  min-width: 180px;
  z-index: 200;
}
.site-header__dropdown-menu--right { left: auto; right: 0; }
.site-header__dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 7px;
  font-size: 14px;
  color: #374151;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s;
}
.site-header__dropdown-item:hover { background: #f8fafc; text-decoration: none; color: #0f172a; }
.site-header__dropdown-item--danger { color: #dc2626; }
.site-header__dropdown-item--danger:hover { background: #fef2f2; }

/* Badge */
.site-header__badge {
  display: inline-flex; align-items: center; justify-content: center;
  background: #dc2626; color: #fff;
  border-radius: 100px; font-size: 10px; font-weight: 700;
  padding: 1px 6px; margin-left: 4px;
}

/* Actions */
.site-header__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.site-header__btn {
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
  white-space: nowrap;
  border: none;
}
.site-header__btn--ghost {
  color: #374151;
  background: transparent;
  border: 1px solid #e2e8f0;
}
.site-header__btn--ghost:hover { background: #f1f5f9; color: #0f172a; text-decoration: none; }
.site-header__btn--primary {
  background: linear-gradient(135deg, #575ecf, #4750b8);
  color: #fff;
  box-shadow: 0 2px 8px rgba(87,94,207,0.3);
}
.site-header__btn--primary:hover { background: linear-gradient(135deg, #4750b8, #3a43a6); color: #fff; text-decoration: none; box-shadow: 0 4px 12px rgba(87,94,207,0.4); }

/* Hamburger */
.site-header__hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: auto;
}
.site-header__hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: #374151;
  border-radius: 2px;
}

/* Mobile menu */
.site-header__mobile {
  border-top: 1px solid #f1f5f9;
  padding: 8px 16px 16px;
}
.site-header__mobile-link {
  display: block;
  padding: 11px 12px;
  border-radius: 8px;
  font-size: 15px;
  color: #374151;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s;
}
.site-header__mobile-link:hover { background: #f1f5f9; text-decoration: none; }
.site-header__mobile-link--primary { color: #fff; background: #0f172a; margin-top: 4px; text-align: center; }
.site-header__mobile-link--primary:hover { background: #1e293b; }
.site-header__mobile-link--danger { color: #dc2626; }
.site-header__mobile-divider { border-top: 1px solid #f1f5f9; margin: 8px 0; }

@media (max-width: 768px) {
  .site-header__nav, .site-header__actions { display: none; }
  .site-header__hamburger { display: flex; }
  .site-header__inner { gap: 0; }
}
</style>
